"use client";

import { postData } from "@/request/api";
import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

function Icon({ name, className = "", fill = false }: { name: string; className?: string; fill?: boolean }) {
  return <span className={`material-symbols-outlined ${fill ? "fill" : ""} ${className}`}>{name}</span>;
}

interface FormValues {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  propertyName: string;
  propertyAddress: string;
  city: string;
  district: string;
  propertyType: string;
  apartmentCount: number;
  bedrooms: number;
  bathrooms: number;
}

function SectionHeader({ icon, title, subtitle }: { icon: string; title: string; subtitle?: string }) {
  return (
    <div className="mb-6 flex items-center gap-3">
      <div className="flex w-10 h-10 shrink-0 items-center justify-center rounded-xl bg-clay-600/10 text-clay-600">
        <Icon name={icon} className="text-[20px]" />
      </div>
      <div>
        <p className="font-bold text-ink-900 text-sm">{title}</p>
        {subtitle && <p className="text-xs text-ink-500 mt-0.5">{subtitle}</p>}
      </div>
      <div className="ms-auto h-px flex-1 bg-clay-100" />
    </div>
  );
}

function Field({ label, error, required, children }: { label: string; error?: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-medium text-ink-900">
        {label}
        {required && <span className="ms-1 text-clay-600">*</span>}
      </label>
      {children}
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
}

const MAX_PHOTOS = 8;

/**
 * Phone photos are 3–10 MB: shrink in the browser (longest side 1600 px, JPEG)
 * before sending — the server re-encodes them to the unit photo size anyway.
 */
const compressPhoto = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new window.Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, 1600 / Math.max(img.naturalWidth, img.naturalHeight));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.naturalWidth * scale);
      canvas.height = Math.round(img.naturalHeight * scale);
      const ctx = canvas.getContext("2d");
      if (!ctx) return reject(new Error("canvas"));
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/jpeg", 0.82));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("decode"));
    };
    img.src = url;
  });

const inputCls = (err?: string) =>
  `w-full rounded-xl border bg-surface px-4 py-3 text-sm text-ink-900 placeholder:text-ink-500/60 outline-none transition-all focus:border-clay-600 focus:ring-2 focus:ring-clay-600/15 ${
    err ? "border-red-400 focus:border-red-400 focus:ring-red-400/10" : "border-clay-100"
  }`;

export default function ListingFormSection({
  isAr,
  prefill,
}: {
  isAr: boolean;
  prefill?: { firstName?: string; lastName?: string; phone?: string; email?: string };
}) {
  const t = (a: string, e: string) => (isAr ? a : e);
  const [submitted, setSubmitted] = useState(false);
  // Owner's photos (compressed data URLs), at most MAX_PHOTOS.
  const [photos, setPhotos] = useState<string[]>([]);
  const [adding, setAdding] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const addPhotos = async (files: FileList | null) => {
    if (!files?.length) return;
    const room = MAX_PHOTOS - photos.length;
    const picked = Array.from(files).filter((f) => f.type.startsWith("image/"));
    if (picked.length > room) toast.error(t(`الحد الأقصى ${MAX_PHOTOS} صور — أُضيفت أول ${Math.max(room, 0)}`, `Up to ${MAX_PHOTOS} photos — added the first ${Math.max(room, 0)}`));
    setAdding(true);
    const out: string[] = [];
    for (const file of picked.slice(0, Math.max(room, 0))) {
      try {
        out.push(await compressPhoto(file));
      } catch {
        toast.error(t(`تعذّر قراءة الصورة «${file.name}»`, `Couldn't read "${file.name}"`));
      }
    }
    setPhotos((p) => [...p, ...out].slice(0, MAX_PHOTOS));
    setAdding(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      firstName: prefill?.firstName ?? "", lastName: prefill?.lastName ?? "",
      phone: prefill?.phone ?? "", email: prefill?.email ?? "",
      propertyName: "", propertyAddress: "", city: "", district: "",
      propertyType: "", apartmentCount: 1, bedrooms: 1, bathrooms: 1,
    },
  });

  const onSubmit = async (data: FormValues) => {
    try {
      await postData("property-requests", { ...data, images: photos });
      setSubmitted(true);
      setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || t("حدث خطأ، حاول مرة أخرى", "Something went wrong, please try again"));
    }
  };

  const propertyTypes = [
    { value: "apartment", ar: "شقة", en: "Apartment" },
    { value: "villa", ar: "فيلا", en: "Villa" },
    { value: "duplex", ar: "دوبلكس", en: "Duplex" },
    { value: "studio", ar: "استوديو", en: "Studio" },
    { value: "chalet", ar: "شاليه", en: "Chalet" },
    { value: "other", ar: "أخرى", en: "Other" },
  ];

  return (
    <section id="listing-form" className="bg-background py-20 md:py-24" dir={isAr ? "rtl" : "ltr"}>
      <div className="container mx-auto max-w-4xl px-6">
        {/* Header */}
        <div className="mb-12 text-center">
          <span className="mb-4 inline-flex items-center gap-2 rounded-full border border-clay-600/30 bg-clay-600/10 px-5 py-2 text-xs font-bold text-clay-600">
            <Icon name="auto_awesome" className="text-[16px]" />
            {t("سجّل وحدتك", "List your property")}
          </span>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-ink-900">
            {t("أرسل طلب الإدراج", "Submit your listing request")}
          </h2>
          <p className="mt-3 text-sm text-ink-500">
            {t("املأ البيانات التالية وسيتواصل معك فريقنا خلال ٢٤ ساعة.", "Fill in the details below and our team will contact you within 24 hours.")}
          </p>
        </div>

        {/* Card */}
        <div className="overflow-hidden rounded-3xl border border-clay-100 bg-surface shadow-sm">
          <div className="h-1 w-full bg-gradient-to-r from-transparent via-clay-600 to-transparent" />
          <div className="p-6 md:p-10">
            {submitted ? (
              <div className="flex flex-col items-center justify-center gap-6 rounded-3xl border border-emerald-200/60 bg-emerald-50/60 px-8 py-16 text-center">
                <div className="relative flex w-24 h-24 items-center justify-center">
                  <div className="absolute inset-0 animate-ping rounded-full bg-emerald-200/40" />
                  <div className="relative flex w-24 h-24 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                    <Icon name="check_circle" fill className="text-5xl" />
                  </div>
                </div>
                <div className="space-y-2">
                  <h3 className="font-display text-2xl font-bold text-ink-900">{t("تم استلام طلبك!", "Request received!")}</h3>
                  <p className="max-w-sm text-sm leading-relaxed text-ink-500">
                    {t("شكراً لك. سيراجع فريق منازل الدرعية طلبك ويتواصل معك خلال ٢٤ ساعة.", "Thank you. The Diriyah Homes team will review your request and contact you within 24 hours.")}
                  </p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-10">
                {/* Personal */}
                <div>
                  <SectionHeader icon="person" title={t("البيانات الشخصية", "Personal information")} subtitle={t("كيف نتواصل معك", "How we reach you")} />
                  <div className="grid gap-5 sm:grid-cols-2">
                    <Field label={t("الاسم الأول", "First name")} error={errors.firstName?.message} required>
                      <input className={inputCls(errors.firstName?.message)} placeholder={t("محمد", "Mohammed")}
                        {...register("firstName", { required: t("الاسم الأول مطلوب", "First name is required"), minLength: { value: 2, message: t("حرفين على الأقل", "At least 2 characters") } })} />
                    </Field>
                    <Field label={t("اسم العائلة", "Last name")} error={errors.lastName?.message} required>
                      <input className={inputCls(errors.lastName?.message)} placeholder={t("العتيبي", "Al-Otaibi")}
                        {...register("lastName", { required: t("اسم العائلة مطلوب", "Last name is required"), minLength: { value: 2, message: t("حرفين على الأقل", "At least 2 characters") } })} />
                    </Field>
                    <Field label={t("رقم الجوال", "Phone")} error={errors.phone?.message} required>
                      <input type="tel" dir="ltr" className={inputCls(errors.phone?.message)} placeholder="05xxxxxxxx"
                        {...register("phone", { required: t("رقم الجوال مطلوب", "Phone is required"), minLength: { value: 9, message: t("رقم غير صالح", "Invalid number") } })} />
                    </Field>
                    <Field label={t("البريد الإلكتروني", "Email")} error={errors.email?.message} required>
                      <input type="email" dir="ltr" className={inputCls(errors.email?.message)} placeholder="name@email.com"
                        {...register("email", { required: t("البريد مطلوب", "Email is required"), pattern: { value: /^\S+@\S+\.\S+$/, message: t("بريد غير صالح", "Invalid email") } })} />
                    </Field>
                  </div>
                </div>

                <div className="h-px bg-clay-100" />

                {/* Property */}
                <div>
                  <SectionHeader icon="apartment" title={t("بيانات الوحدة", "Property information")} subtitle={t("عرّفنا بوحدتك", "Tell us about your property")} />
                  <div className="grid gap-5 sm:grid-cols-2">
                    <Field label={t("اسم الوحدة", "Property name")} error={errors.propertyName?.message} required>
                      <input className={inputCls(errors.propertyName?.message)} placeholder={t("فيلا الياسمين", "Al-Yasmin Villa")}
                        {...register("propertyName", { required: t("اسم الوحدة مطلوب", "Property name is required"), minLength: { value: 3, message: t("3 أحرف على الأقل", "At least 3 characters") } })} />
                    </Field>
                    <Field label={t("عنوان الوحدة", "Property address")} error={errors.propertyAddress?.message} required>
                      <input className={inputCls(errors.propertyAddress?.message)} placeholder={t("شارع الملك عبدالعزيز", "King Abdulaziz St.")}
                        {...register("propertyAddress", { required: t("العنوان مطلوب", "Address is required"), minLength: { value: 5, message: t("عنوان غير مكتمل", "Address too short") } })} />
                    </Field>
                    <Field label={t("نوع الوحدة", "Property type")} error={errors.propertyType?.message} required>
                      <select className={inputCls(errors.propertyType?.message)} defaultValue="" {...register("propertyType", { required: t("النوع مطلوب", "Type is required") })}>
                        <option value="" disabled>{t("اختر النوع", "Select type")}</option>
                        {propertyTypes.map((o) => (
                          <option key={o.value} value={o.value}>{t(o.ar, o.en)}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label={t("عدد الوحدات", "Number of units")} error={errors.apartmentCount?.message} required>
                      <input type="number" min={1} className={inputCls(errors.apartmentCount?.message)}
                        {...register("apartmentCount", { required: t("مطلوب", "Required"), min: { value: 1, message: t("1 على الأقل", "At least 1") }, valueAsNumber: true })} />
                    </Field>
                  </div>
                </div>

                <div className="h-px bg-clay-100" />

                {/* Location */}
                <div>
                  <SectionHeader icon="location_on" title={t("الموقع", "Location")} subtitle={t("أين تقع وحدتك", "Where it's located")} />
                  <div className="grid gap-5 sm:grid-cols-2">
                    <Field label={t("المدينة", "City")} error={errors.city?.message} required>
                      <input className={inputCls(errors.city?.message)} placeholder={t("الرياض", "Riyadh")}
                        {...register("city", { required: t("المدينة مطلوبة", "City is required") })} />
                    </Field>
                    <Field label={t("الحي", "District")} error={errors.district?.message} required>
                      <input className={inputCls(errors.district?.message)} placeholder={t("حي الطريف", "At-Turaif")}
                        {...register("district", { required: t("الحي مطلوب", "District is required") })} />
                    </Field>
                  </div>
                </div>

                <div className="h-px bg-clay-100" />

                {/* Unit details */}
                <div>
                  <SectionHeader icon="grid_view" title={t("تفاصيل الوحدة", "Unit details")} subtitle={t("الغرف والمرافق", "Rooms & facilities")} />
                  <div className="grid gap-5 sm:grid-cols-3">
                    <Field label={t("غرف النوم", "Bedrooms")} error={errors.bedrooms?.message} required>
                      <input type="number" min={0} className={inputCls(errors.bedrooms?.message)}
                        {...register("bedrooms", { required: t("مطلوب", "Required"), min: { value: 0, message: t("0 أو أكثر", "0 or more") }, valueAsNumber: true })} />
                    </Field>
                    <Field label={t("دورات المياه", "Bathrooms")} error={errors.bathrooms?.message} required>
                      <input type="number" min={1} className={inputCls(errors.bathrooms?.message)}
                        {...register("bathrooms", { required: t("مطلوب", "Required"), min: { value: 1, message: t("1 على الأقل", "At least 1") }, valueAsNumber: true })} />
                    </Field>
                    <div className="flex items-end pb-0.5">
                      <div className="w-full rounded-xl border border-clay-600/20 bg-clay-600/5 px-4 py-3.5 text-xs leading-relaxed text-ink-500">
                        💡 {t("سيساعدك فريقنا في تحديد أفضل سعر لوحدتك.", "Our team will help set the best price for your property.")}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="h-px bg-clay-100" />

                {/* Photos */}
                <div>
                  <SectionHeader
                    icon="photo_library"
                    title={t("صور الوحدة", "Property photos")}
                    subtitle={t(`اختياري — حتى ${MAX_PHOTOS} صور`, `Optional — up to ${MAX_PHOTOS} photos`)}
                  />
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    multiple
                    hidden
                    onChange={(e) => addPhotos(e.target.files)}
                  />
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    {photos.map((src, i) => (
                      <div key={i} className="group relative aspect-[4/3] overflow-hidden rounded-xl border border-clay-100 bg-clay-50">
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img src={src} alt="" className="h-full w-full object-cover" />
                        {i === 0 && (
                          <span className="absolute bottom-1.5 inset-s-1.5 rounded-full bg-shade/70 px-2 py-0.5 text-[10px] font-bold text-white">
                            {t("الرئيسية", "Cover")}
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => setPhotos((p) => p.filter((_, k) => k !== i))}
                          aria-label={t("حذف الصورة", "Remove photo")}
                          className="absolute top-1.5 inset-e-1.5 flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-red-600 shadow"
                        >
                          <Icon name="close" className="text-[16px]" />
                        </button>
                      </div>
                    ))}
                    {photos.length < MAX_PHOTOS && (
                      <button
                        type="button"
                        onClick={() => fileRef.current?.click()}
                        disabled={adding}
                        className="flex aspect-[4/3] flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-clay-100 text-ink-500 transition-colors hover:border-clay-600 hover:text-clay-600 disabled:opacity-60"
                      >
                        <Icon name={adding ? "progress_activity" : "add_photo_alternate"} className={`text-[26px] ${adding ? "animate-spin" : ""}`} />
                        <span className="text-xs font-bold">{adding ? t("جارٍ التجهيز…", "Preparing…") : t("إضافة صور", "Add photos")}</span>
                        <span className="text-[10px]">{photos.length}/{MAX_PHOTOS}</span>
                      </button>
                    )}
                  </div>
                </div>

                {/* Submit */}
                <div className="flex flex-col items-center gap-4 pt-2">
                  <button type="submit" disabled={isSubmitting || adding}
                    className="flex min-w-48 items-center justify-center gap-3 rounded-2xl bg-clay-600 px-10 py-4 text-sm font-bold text-white shadow-lg shadow-clay-600/30 transition-all hover:bg-clay-700 disabled:cursor-not-allowed disabled:opacity-60">
                    {isSubmitting ? (
                      <>
                        <Icon name="progress_activity" className="animate-spin text-[18px]" />
                        {t("جارٍ الإرسال…", "Sending…")}
                      </>
                    ) : (
                      <>
                        <Icon name="send" className="text-[18px]" />
                        {t("إرسال الطلب", "Send request")}
                      </>
                    )}
                  </button>
                  <p className="max-w-xs text-center text-xs text-ink-500">
                    {t("بإرسالك الطلب فإنك توافق على سياسة الخصوصية وشروط الاستخدام.", "By submitting, you agree to our privacy policy and terms of use.")}
                  </p>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
